// Tiny IndexedDB wrapper for caching the last N Personal Map pin sets.
// Keeps the offline layer dependency-free (no idb-keyval / dexie). Called
// from NearbyMap.jsx: `savePinSet` after a successful fetch, `loadPinSet`
// when the fetch fails or the browser is offline.
//
// Storage shape:  db='fn_offline'  →  store='pins'  →  { key, value:{pins, meta, ts} }
//   key   = deterministic hash of (rounded lat, rounded lng, sorted cats, radius)
//   value = { pins, center:[lat,lng], radius, cats:[...], meta:{engine,summary}, ts }
// We LRU-trim to MAX_ENTRIES to keep IndexedDB under ~2 MB per user.

const DB_NAME      = "fn_offline";
const STORE_PINS   = "pins";
const DB_VERSION   = 1;
const MAX_ENTRIES  = 10;     // last 10 distinct pin sets
const MAX_AGE_MS   = 7 * 24 * 60 * 60 * 1000;   // auto-expire after 7 days

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("no-idb"));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_PINS)) {
        const store = db.createObjectStore(STORE_PINS, { keyPath: "key" });
        store.createIndex("ts", "ts");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

// Deterministic-ish cache key. Rounded to 2 dp of lat/lng so nearby moves
// share the same cache entry (~1 km resolution). Cats sorted for stability.
export function cacheKeyFor({ lat, lng, cats = [], radius = 3 }) {
  const rl = (Math.round((lat || 0) * 100) / 100).toFixed(2);
  const rg = (Math.round((lng || 0) * 100) / 100).toFixed(2);
  const c  = [...cats].sort().join("|") || "all";
  return `${rl}:${rg}:${radius}:${c}`;
}

export async function savePinSet(key, { pins, center, radius, cats, meta }) {
  if (!Array.isArray(pins) || !key) return;
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_PINS, "readwrite");
    tx.objectStore(STORE_PINS).put({
      key, pins, center, radius, cats: cats || [],
      meta: meta || {}, ts: Date.now(),
    });
    await new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = () => rej(tx.error); });
    // LRU trim: keep newest MAX_ENTRIES, drop expired.
    await trimOldEntries(db);
    db.close();
  } catch { /* silent — offline cache is best-effort */ }
}

export async function loadPinSet(key) {
  if (!key) return null;
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_PINS, "readonly");
    const req = tx.objectStore(STORE_PINS).get(key);
    const row = await new Promise((res, rej) => {
      req.onsuccess = () => res(req.result); req.onerror = () => rej(req.error);
    });
    db.close();
    if (!row) return null;
    if (Date.now() - row.ts > MAX_AGE_MS) return null;
    return row;
  } catch { return null; }
}

// Return the most-recently cached pin set regardless of key — used when the
// map has no obvious match but the user still wants "something offline".
export async function loadMostRecentPinSet() {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_PINS, "readonly");
    const store = tx.objectStore(STORE_PINS);
    const rows = await new Promise((res, rej) => {
      const req = store.getAll();
      req.onsuccess = () => res(req.result || []); req.onerror = () => rej(req.error);
    });
    db.close();
    if (!rows.length) return null;
    rows.sort((a, b) => b.ts - a.ts);
    return rows[0];
  } catch { return null; }
}

async function trimOldEntries(db) {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_PINS, "readwrite");
      const store = tx.objectStore(STORE_PINS);
      const req = store.getAll();
      req.onsuccess = () => {
        const rows = req.result || [];
        // Drop expired
        const now = Date.now();
        const alive = rows.filter(r => (now - r.ts) < MAX_AGE_MS);
        const expired = rows.filter(r => (now - r.ts) >= MAX_AGE_MS);
        expired.forEach(r => store.delete(r.key));
        // LRU trim
        if (alive.length > MAX_ENTRIES) {
          alive.sort((a, b) => b.ts - a.ts);
          alive.slice(MAX_ENTRIES).forEach(r => store.delete(r.key));
        }
        tx.oncomplete = () => resolve();
      };
      req.onerror = () => resolve();
    } catch { resolve(); }
  });
}

// -------- Stats / clear utilities for Settings → Storage panel ---------
// Kept in this same module so Settings never touches the DB structure directly.

// Return { count, totalPins, newestTs, oldestTs } for the offline pin store.
export async function pinCacheStats() {
  const empty = { count: 0, totalPins: 0, newestTs: null, oldestTs: null };
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_PINS, "readonly");
    const rows = await new Promise((res, rej) => {
      const r = tx.objectStore(STORE_PINS).getAll();
      r.onsuccess = () => res(r.result || []); r.onerror = () => rej(r.error);
    });
    db.close();
    if (!rows.length) return empty;
    const stats = rows.reduce((acc, r) => {
      acc.count += 1;
      acc.totalPins += Array.isArray(r.pins) ? r.pins.length : 0;
      if (!acc.newestTs || r.ts > acc.newestTs) acc.newestTs = r.ts;
      if (!acc.oldestTs || r.ts < acc.oldestTs) acc.oldestTs = r.ts;
      return acc;
    }, { count: 0, totalPins: 0, newestTs: null, oldestTs: null });
    return stats;
  } catch { return empty; }
}

// Delete ONLY the offline-pins IDB store (do not touch anything else).
export async function clearPinCache() {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_PINS, "readwrite");
    tx.objectStore(STORE_PINS).clear();
    await new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = () => rej(tx.error); });
    db.close();
    return true;
  } catch { return false; }
}
